// 3rd party libs
import React from 'react';
import { Box, Typography, CircularProgress, CircularProgressProps, Tooltip } from '@mui/material';

// local modules
import StorageIcon from '../../../assets/icons/storage.svg';
import styles from './GigsCount.module.scss';

type TGigsCount = {
  usage: number;
  limit?: number;
  hours?: boolean;
  withPercentage?: boolean;
  widthD?: number;
  enforcePercentage?: boolean;
  disableTooltip?: boolean;
};

const roundedPercentage = (part: number, total: number, decimalPlaces: number) => {
  const result = (part / total) * 100;
  return result.toFixed(decimalPlaces);
};

function CircularProgressWithLabel(props: CircularProgressProps & { value: number; total: number }) {
  const value = (props.value / props.total) * 100;

  return (
    <Box sx={{ position: 'relative', display: 'inline-flex' }}>
      <CircularProgress variant="determinate" color="inherit" {...props} value={value} />
      <Box
        sx={{
          top: 0,
          left: 0,
          bottom: 0,
          right: 0,
          position: 'absolute',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'white',
        }}
      >
        <Typography variant="caption" component="div" fontSize={'0.65rem'}>
          {`${roundedPercentage(props.value, props.total, 1)}%`}
        </Typography>
      </Box>
    </Box>
  );
}

const GigsCount: React.FC<TGigsCount> = ({ usage, limit, hours, withPercentage, widthD, enforcePercentage, disableTooltip }) => {
  let formattedUsage = usage % 1 !== 0 ? usage.toFixed(1) : usage;
  if (Number(formattedUsage) === 0 && usage > 0) {
    formattedUsage = 0.1;
  }

  const unit = hours ? 'Hrs' : 'GB';
  const percentageText = limit && withPercentage ? ` (${Math.round((usage / limit) * 100)}%)` : '';
  const label = `${formattedUsage}${limit ? `/${limit}` : ''} ${unit}${percentageText}`;

  if (enforcePercentage && limit) {
    const progress = (
      <div>
        <CircularProgressWithLabel value={usage} total={limit} />
      </div>
    );
    return disableTooltip ? (
      progress
    ) : (
      <Tooltip title={`${label}`}>
        {progress}
      </Tooltip>
    );
  }

  // Wide screen: show icon and text
  if (widthD && widthD > 1476) {
    return (
      <>
        <img className={styles.img} src={StorageIcon} alt="folder count" />
        <span>{label}</span>
      </>
    );
  }

  // Narrow screen: show progress if limit exists
  if (limit && widthD && widthD <= 1476) {
    const progress = (
      <div>
        <CircularProgressWithLabel value={usage} total={limit} />
      </div>
    );
    return disableTooltip ? (
      progress
    ) : (
      <Tooltip title={`${label}`}>
        {progress}
      </Tooltip>
    );
  }

  // Fallback
  return (
    <>
      <img className={styles.img} src={StorageIcon} alt="folder count" />
      <span>{label}</span>
    </>
  );
};

export default GigsCount;
